import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import TTSAudioPlayer from "@/components/practice/TTSAudioPlayer";
import { PenLine, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function DictationTab() {
  const [session, setSession] = useState(null);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [answer, setAnswer] = useState("");
  const [checked, setChecked] = useState(false);
  const [result, setResult] = useState(null);

  const startMutation = useMutation({
    mutationFn: () => api.post("/ai/dictation/start", null, { params: { count: 5 } }),
    onSuccess: ({ data }) => {
      setSession(data);
      setIndex(0);
      setAnswers([]);
      setAnswer("");
      setChecked(false);
      setResult(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Не удалось создать диктант"),
  });

  const submitMutation = useMutation({
    mutationFn: (allAnswers) => api.post(`/ai/dictation/${session.id}/submit`, { answers: allAnswers }),
    onSuccess: ({ data }) => setResult(data),
  });

  const next = () => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);
    setAnswer("");
    setChecked(false);
    if (index + 1 >= session.sentences.length) {
      submitMutation.mutate(newAnswers);
    } else {
      setIndex((i) => i + 1);
    }
  };

  if (!session) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center" data-testid="dictation-start-state">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
          <PenLine className="w-7 h-7 text-primary" />
        </div>
        <p className="font-medium">Прослушайте предложение и напечатайте его</p>
        <Button onClick={() => startMutation.mutate()} disabled={startMutation.isPending} data-testid="start-dictation-button">
          {startMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Начать диктант"}
        </Button>
      </div>
    );
  }

  if (result) {
    return (
      <div className="max-w-lg mx-auto py-6 space-y-4" data-testid="dictation-result-state">
        <p className="font-heading text-xl font-bold text-center">Средний результат: {result.score.average_percent}%</p>
        <div className="space-y-2">
          {result.results.map((r, i) => (
            <div
              key={i}
              className={`flex items-start gap-2 rounded-lg p-3 text-sm ${r.is_correct ? "bg-emerald-50 dark:bg-emerald-900/20" : "bg-orange-50 dark:bg-orange-900/20"}`}
              data-testid={`dictation-result-item-${i}`}
            >
              {r.is_correct ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-foreground">{r.sentence}</p>
                  <span className="shrink-0 font-medium">{r.match_percent}%</span>
                </div>
                {!r.is_correct && <p className="text-muted-foreground">Вы написали: <span className="line-through">{r.user_answer || "—"}</span></p>}
              </div>
            </div>
          ))}
        </div>
        <Button className="w-full" onClick={() => setSession(null)} data-testid="dictation-restart-button">Новый диктант</Button>
      </div>
    );
  }

  const sentence = session.sentences[index];

  return (
    <div className="max-w-lg mx-auto py-6 space-y-5" data-testid="dictation-active-state">
      <Progress value={(index / session.sentences.length) * 100} className="h-1.5" />
      <p className="text-sm text-muted-foreground text-center">{index + 1} / {session.sentences.length}</p>
      <div className="bg-card border border-border rounded-2xl p-6 space-y-4 text-center">
        <TTSAudioPlayer text={sentence} testIdPrefix={`dictation-audio-${index}`} key={index} />
        {!checked ? (
          <form onSubmit={(e) => { e.preventDefault(); setChecked(true); }} className="space-y-3">
            <Input autoFocus value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Напечатайте предложение..." data-testid="dictation-answer-input" />
            <Button type="submit" className="w-full" disabled={!answer.trim()} data-testid="dictation-check-button">Проверить</Button>
          </form>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Эталон: <span className="font-medium text-foreground">{sentence}</span></p>
            <Button className="w-full" onClick={next} disabled={submitMutation.isPending} data-testid="dictation-next-button">
              {submitMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (index + 1 >= session.sentences.length ? "Завершить" : "Далее")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
